import { useCallback, useEffect, useRef, useState } from 'react';
import { auth } from '../auth/firebase.js';
import i18n, { normalizeLang } from '../i18n/index.js';

/**
 * useSSEChat — SSE 對話 hook
 *   - sendMessage(text, files?) → POST /ivr-builder/api/ivr/sessions/:sid/messages
 *   - abort() → AbortController.abort() (server 端 SIGTERM claude)
 *   - messages: history + 進行中 user + 進行中 assistant，永遠 user 在 assistant 前
 *
 * 設計：用 ref 累積 currentTurn 的 streaming state，render 時用 state mirror（每次 SSE
 * event 都 setState 觸發 render）。SSE 結束時用 ref 的最新值一次 push 進 history，
 * 避免在 setState updater 內套娃 setHistory 的 race condition。
 */
export function useSSEChat(sessionId, opts = {}) {
    // sendUrl override：super admin 運維對話送 /ivr-builder/api/admin/tenants/:hostId/chat，
    // 歷史/stream/abort 仍走 sessionId（sa-<hostId>）共用端點
    const sendUrl = opts.sendUrl || null;
    const [history, setHistory] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [currentTurn, setCurrentTurn] = useState(null);
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState(null);
    const [lastCompletedAt, setLastCompletedAt] = useState(0);    // 任何 turn 結束 → 觸發外部 reload (Sources / WorkspaceInfo)
    // 這一輪開始的時間戳 — 給進度列算「已耗時 / 預估剩餘」。turn 結束不清空,
    // 讓 UI 在 isStreaming 落下的那一刻還能算出這輪總耗時(拿去餵 ETA 的歷史中位數)。
    const [turnStartedAt, setTurnStartedAt] = useState(0);
    const [hasMore, setHasMore] = useState(false);               // 還有更舊的對話可往上載入
    const [loadingOlder, setLoadingOlder] = useState(false);
    const controllerRef = useRef(null);
    const turnRef = useRef(null);    // 鏡像 currentTurn，給 finally commit 用
    const turnStartedRef = useRef(0); // 鏡像 turnStartedAt；turn 結束歸 0，代表「目前沒有進行中的 turn」
    const oldestTsRef = useRef(null); // 目前 history 最舊一則的 ISO timestamp（往前翻頁的 cursor）

    // 開場 / reload：抓「最新一批」（非最舊）。長 session（>limit 則）開場就看到近期對話，
    // 往上滑再 loadOlder 載入更舊的。舊版抓最舊 100 則 → 近期對話消失。
    const loadHistory = useCallback(async () => {
        if (!sessionId) return;
        try {
            const idToken = await auth.currentUser?.getIdToken();
            const res = await fetch(
                `/ivr-builder/api/ivr/sessions/${encodeURIComponent(sessionId)}/messages?limit=30`,
                { headers: { Authorization: `Bearer ${idToken}` } }
            );
            if (!res.ok) throw new Error(`history fetch ${res.status}`);
            const data = await res.json();
            const msgs = data.messages || [];
            setHistory(msgs);
            setHasMore(!!data.hasMore);
            oldestTsRef.current = msgs[0]?.timestamp || null;
        } catch (e) {
            console.warn('[useSSEChat] loadHistory failed:', e.message);
        }
    }, [sessionId]);

    // 往上滑載入更舊的對話 — 用目前最舊一則的 timestamp 當 cursor，prepend 進 history
    const loadOlder = useCallback(async () => {
        if (!sessionId || !oldestTsRef.current || loadingOlder) return;
        setLoadingOlder(true);
        try {
            const idToken = await auth.currentUser?.getIdToken();
            const res = await fetch(
                `/ivr-builder/api/ivr/sessions/${encodeURIComponent(sessionId)}/messages?limit=30&before=${encodeURIComponent(oldestTsRef.current)}`,
                { headers: { Authorization: `Bearer ${idToken}` } }
            );
            if (!res.ok) throw new Error(`older fetch ${res.status}`);
            const data = await res.json();
            const older = data.messages || [];
            if (older.length) {
                oldestTsRef.current = older[0].timestamp || oldestTsRef.current;
                setHistory((h) => [...older, ...h]);
            }
            setHasMore(!!data.hasMore);
        } catch (e) {
            console.warn('[useSSEChat] loadOlder failed:', e.message);
        } finally {
            setLoadingOlder(false);
        }
    }, [sessionId, loadingOlder]);

    function updateTurn(updater) {
        turnRef.current = updater(turnRef.current);
        setCurrentTurn(turnRef.current);
    }

    /**
     * attachReplay — 用 transcript-replay 還原 mid-session 的 thinking/tool/text 事件。
     * 用戶離開該畫面又回來時呼叫；replay 是「目前 transcript 的快照」（不會 tail 後續新事件）。
     * replay 完若 isRunning=true → 保留 streaming 狀態 + 輪詢 Firestore 等最終 assistant msg。
     */
    const attachReplay = useCallback(async () => {
        if (!sessionId) return;
        setIsStreaming(true);
        // replay 是「接回一個已經在跑的 turn」— 起算點只能用 attach 當下,顯示的已耗時會偏短。
        // 若是 sendMessage 斷線後轉進來的,沿用原本的起算點(ref 還沒歸 0),不要把計時器歸零。
        if (!turnStartedRef.current) {
            turnStartedRef.current = Date.now();
            setTurnStartedAt(turnStartedRef.current);
        }
        turnRef.current = { role: 'assistant', text: '', thinking: '', toolCalls: [], statusNote: '', status: 'running' };
        setCurrentTurn(turnRef.current);

        function applyReplayEvent(ev) {
            switch (ev.type) {
                case 'status':
                    // 後端 pushStatus()：壓縮記憶等「沒有 text/thinking/tool 事件」的長時間
                    // 動作，用一行暫時性狀態列讓用戶知道還在跑（done=true 就清掉）。
                    updateTurn((cur) => ({ ...cur, statusNote: ev.done ? '' : (ev.message || '') }));
                    break;
                case 'thinking_delta':
                    updateTurn((cur) => ({ ...cur, thinking: (cur?.thinking || '') + (ev.text || '') }));
                    break;
                case 'text_delta':
                    updateTurn((cur) => ({ ...cur, text: (cur?.text || '') + (ev.text || '') }));
                    break;
                case 'tool_start':
                    updateTurn((cur) => ({ ...cur, toolCalls: [...(cur?.toolCalls || []), { name: ev.name, input: '', result: null }] }));
                    break;
                case 'tool_input_delta':
                    updateTurn((cur) => {
                        const tc = [...(cur?.toolCalls || [])];
                        if (tc.length) tc[tc.length - 1] = { ...tc[tc.length - 1], input: (tc[tc.length - 1].input || '') + (ev.json || '') };
                        return { ...cur, toolCalls: tc };
                    });
                    break;
                case 'tool_result':
                    updateTurn((cur) => {
                        const tc = [...(cur?.toolCalls || [])];
                        if (tc.length && tc[tc.length - 1].result === null) {
                            tc[tc.length - 1] = { ...tc[tc.length - 1], result: ev.content || '' };
                        }
                        return { ...cur, toolCalls: tc };
                    });
                    break;
            }
        }

        // 單一 SSE 連線到 /sessions/:sid/stream — server 先 replay buffered events，
        // 後續 live push（在 claude 還在跑的話）。連線維持到 claude 跑完或收到 complete。
        // 自己的 AbortController 寫進 controllerRef → 用戶按 stop 時 abort() 也能切這條
        const streamCtl = new AbortController();
        controllerRef.current = streamCtl;
        let sawTerminal = false;
        try {
            const idToken = await auth.currentUser?.getIdToken();
            const res = await fetch(
                `/ivr-builder/api/ivr/sessions/${encodeURIComponent(sessionId)}/stream`,
                { headers: { Authorization: `Bearer ${idToken}` }, signal: streamCtl.signal }
            );
            if (!res.ok || !res.body) {
                // 404 = session 不在 in-memory（容器重啟後）→ fallback 一次性 transcript replay
                if (res.status === 404) {
                    const fb = await fetch(
                        `/ivr-builder/api/ivr/sessions/${encodeURIComponent(sessionId)}/replay`,
                        { headers: { Authorization: `Bearer ${idToken}` }, signal: streamCtl.signal }
                    );
                    if (fb.ok && fb.body) {
                        const reader = fb.body.getReader();
                        const decoder = new TextDecoder();
                        let buf = '';
                        while (true) {
                            const { done, value } = await reader.read();
                            if (done) break;
                            buf += decoder.decode(value, { stream: true });
                            let idx;
                            while ((idx = buf.indexOf('\n\n')) >= 0) {
                                const raw = buf.slice(0, idx);
                                buf = buf.slice(idx + 2);
                                const line = raw.split('\n').find((l) => l.startsWith('data: '));
                                if (!line) continue;
                                try {
                                    const ev = JSON.parse(line.slice(6));
                                    if (ev.type !== 'replay-complete') applyReplayEvent(ev);
                                } catch { /* skip */ }
                            }
                        }
                    }
                } else {
                    throw new Error(`stream fetch ${res.status}`);
                }
            } else {
                const reader = res.body.getReader();
                const decoder = new TextDecoder();
                let buf = '';
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buf += decoder.decode(value, { stream: true });
                    let idx;
                    while ((idx = buf.indexOf('\n\n')) >= 0) {
                        const raw = buf.slice(0, idx);
                        buf = buf.slice(idx + 2);
                        const line = raw.split('\n').find((l) => l.startsWith('data: '));
                        if (!line) continue;
                        try {
                            const ev = JSON.parse(line.slice(6));
                            if (ev.type === 'replay-complete') continue;
                            applyReplayEvent(ev);
                            // 終結事件：直接記下狀態 + 標記 sawTerminal，loop 等 server res.end → done=true 退出
                            if (ev.type === 'complete') {
                                sawTerminal = true;
                                updateTurn((cur) => cur ? { ...cur, status: ev.status || 'completed', exitCode: ev.exitCode, failReason: ev.reason || null, failDetail: ev.detail || null } : cur);
                            } else if (ev.type === 'error') {
                                sawTerminal = true;
                                updateTurn((cur) => cur ? { ...cur, status: 'failed' } : cur);
                            }
                        } catch { /* skip */ }
                    }
                }
            }
        } catch (e) {
            if (e.name === 'AbortError') {
                // 用戶按 stop 中斷 stream — 標記 turn 為 interrupted
                updateTurn((cur) => cur ? { ...cur, status: 'interrupted' } : cur);
            } else {
                console.warn('[useSSEChat] attachReplay stream failed:', e.message);
            }
        }

        // 結束：commit replayed turn 進 history（保留完整 thinking/tool/text trail）
        // 沿用 turnRef 上記錄的 status(interrupted / timed_out / failed / completed),
        // 沒 terminal event 才 fallback 'completed'。
        const finalTurn = turnRef.current;
        if (finalTurn && (finalTurn.text || finalTurn.thinking || finalTurn.toolCalls?.length)) {
            setHistory((h) => [...h, { ...finalTurn, status: finalTurn.status || 'completed' }]);
        }
        controllerRef.current = null;
        // 若 server 剛 close 且 assistant msg 也已寫進 Firestore，重新拉 history 確保有最終版
        try { await loadHistory(); } catch { /* ignore */ }
        turnRef.current = null;
        turnStartedRef.current = 0;
        setCurrentTurn(null);
        setIsStreaming(false);
        setLastCompletedAt(Date.now());
    }, [sessionId, loadHistory]);

    // Mount: load history, then 若有正在跑的 update → attach replay 重現 mid-flight 事件
    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;
        (async () => {
            await loadHistory();
            if (cancelled) return;
            try {
                const idToken = await auth.currentUser?.getIdToken();
                const r = await fetch('/ivr-builder/api/ivr/sessions/active', {
                    headers: { Authorization: `Bearer ${idToken}` },
                });
                if (!r.ok || cancelled) return;
                const data = await r.json();
                const isActive = (data.sessions || []).some((s) => s.sessionId === sessionId);
                if (isActive && !cancelled) attachReplay();
            } catch (e) {
                console.warn('[useSSEChat] active-check failed:', e.message);
            }
        })();
        return () => { cancelled = true; };
    }, [sessionId, loadHistory, attachReplay]);

    // sendMessage(text, opts) — opts: { files?, displayText? }
    //   text        = 完整指令（含 skill 名稱等，AI 真正執行的內容）
    //   displayText = 純用戶需求摘要，只給 UI 顯示用；AI 不會看到。沒給就顯示 text
    const sendMessage = useCallback(async (text, opts = {}) => {
        const { files, displayText } = opts;
        if (!sessionId || (!text?.trim() && !files?.length) || isStreaming) return;
        setError(null);
        setIsStreaming(true);
        turnStartedRef.current = Date.now();
        setTurnStartedAt(turnStartedRef.current);
        const userMsg = {
            role: 'user',
            text: text?.trim() || '(附檔案)',
            displayText: displayText || null,
            files: files ? Array.from(files).map(f => ({ name: f.name, size: f.size })) : [],
            timestamp: new Date().toISOString(),
        };
        setCurrentUser(userMsg);
        turnRef.current = {
            role: 'assistant',
            text: '',
            thinking: '',
            toolCalls: [],
            status: 'running',
        };
        setCurrentTurn(turnRef.current);

        const controller = new AbortController();
        controllerRef.current = controller;
        let serverUserMessageId = null;   // server 寫進 Firestore 後 echo 給我們的 id（恢復用）
        let reAttached = false;           // SSE 斷線後是否已改走 attachReplay 續接（true → 跳過 finally 的 commit）

        function applyEvent(ev) {
            const t = turnRef.current;
            if (!t) return;
            switch (ev.type) {
                case 'message_saved':
                    if (ev.role === 'user' && ev.messageId) serverUserMessageId = ev.messageId;
                    break;
                case 'status':
                    // 後端 pushStatus()：壓縮記憶等「沒有 text/thinking/tool 事件」的長時間
                    // 動作，用一行暫時性狀態列讓用戶知道還在跑（done=true 就清掉）。
                    updateTurn((cur) => ({ ...cur, statusNote: ev.done ? '' : (ev.message || '') }));
                    break;
                case 'text_delta':
                    updateTurn((cur) => ({ ...cur, text: (cur.text || '') + (ev.text || '') }));
                    break;
                case 'thinking_delta':
                    updateTurn((cur) => ({ ...cur, thinking: (cur.thinking || '') + (ev.text || '') }));
                    break;
                case 'tool_start':
                    updateTurn((cur) => ({ ...cur, toolCalls: [...(cur.toolCalls || []), { name: ev.name, input: '', result: null }] }));
                    break;
                case 'tool_input_delta':
                    updateTurn((cur) => {
                        const tc = [...(cur.toolCalls || [])];
                        if (tc.length) tc[tc.length - 1] = { ...tc[tc.length - 1], input: (tc[tc.length - 1].input || '') + (ev.json || '') };
                        return { ...cur, toolCalls: tc };
                    });
                    break;
                case 'tool_result':
                    updateTurn((cur) => {
                        const tc = [...(cur.toolCalls || [])];
                        if (tc.length && tc[tc.length - 1].result === null) {
                            tc[tc.length - 1] = { ...tc[tc.length - 1], result: ev.content || '' };
                        }
                        return { ...cur, toolCalls: tc };
                    });
                    break;
                case 'result':
                    updateTurn((cur) => ({ ...cur, finalResult: ev }));
                    break;
                case 'complete':
                    updateTurn((cur) => ({ ...cur, statusNote: '', status: ev.status || 'completed', exitCode: ev.exitCode, failReason: ev.reason || null, failDetail: ev.detail || null }));
                    break;
                case 'error':
                    updateTurn((cur) => ({ ...cur, status: 'failed' }));
                    break;
            }
        }

        try {
            const idToken = await auth.currentUser?.getIdToken();
            const headers = { Authorization: `Bearer ${idToken}` };
            // 介面語系 → 讓 spawn 的 builder Claude 用同語系回覆
            const uiLang = normalizeLang(i18n.language);
            let body;
            if (files?.length) {
                body = new FormData();
                body.append('text', text?.trim() || '');
                if (displayText) body.append('displayText', displayText);
                body.append('language', uiLang);
                for (const f of files) body.append('files', f);
            } else {
                headers['Content-Type'] = 'application/json';
                body = JSON.stringify({ text: text.trim(), language: uiLang, ...(displayText ? { displayText } : {}) });
            }
            const res = await fetch(
                sendUrl || `/ivr-builder/api/ivr/sessions/${encodeURIComponent(sessionId)}/messages`,
                { method: 'POST', headers, body, signal: controller.signal }
            );
            if (!res.ok || !res.body) throw new Error(`SSE start ${res.status}`);

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buf = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                let idx;
                while ((idx = buf.indexOf('\n\n')) >= 0) {
                    const raw = buf.slice(0, idx);
                    buf = buf.slice(idx + 2);
                    const line = raw.split('\n').find((l) => l.startsWith('data: '));
                    if (!line) continue;
                    try {
                        applyEvent(JSON.parse(line.slice(6)));
                    } catch { /* skip */ }
                }
            }
        } catch (e) {
            if (e.name === 'AbortError') {
                updateTurn((cur) => cur ? { ...cur, status: 'interrupted' } : cur);
            } else {
                // 網路斷掉 / SSE 中斷 — claude 在 server 端有 15min grace 還在跑（長 build 5-10min）。
                // 舊行為「只 poll Firestore 60s 就放棄」對長 build 一定等不到 → 顯示失敗、build 被白白 SIGTERM。
                // 改：重新 attach 回 live /stream（與「離開該畫面再回來」同一路徑）—
                //   server 會 replay 已 buffer 的事件 + 續推 live，直到 build 真正 complete/error。
                //   serverUserMessageId 保留供未來除錯（不再靠它輪詢）。
                console.warn('[useSSEChat] stream interrupted, re-attaching to live /stream:', e.message, serverUserMessageId || '');
                // user 訊息先進 history（attachReplay 只負責 assistant turn）；清掉半截 in-flight turn
                setHistory((h) => [...h, userMsg]);
                turnRef.current = null;
                setCurrentTurn(null);
                setCurrentUser(null);
                controllerRef.current = null;
                reAttached = true;
                await attachReplay();   // 自帶 isStreaming / replay+live / commit / cleanup
            }
        } finally {
            // reAttached 時 attachReplay 已接手（自己 commit + 清狀態），這裡不要再 commit 一次
            if (!reAttached) {
                // 一次 commit user + assistant 進 history（保證順序 user → assistant）
                const finalTurn = turnRef.current;
                setHistory((h) => {
                    const out = [...h];
                    out.push(userMsg);
                    if (finalTurn) out.push(finalTurn);
                    return out;
                });
                turnRef.current = null;
                turnStartedRef.current = 0;
                setCurrentTurn(null);
                setCurrentUser(null);
                setIsStreaming(false);
                setLastCompletedAt(Date.now());
                controllerRef.current = null;
            }
        }
    }, [sessionId, sendUrl, isStreaming, attachReplay]);

    // abort = (1) 切斷本地 SSE 連線 (2) 呼 server endpoint SIGTERM claude proc。
    // (2) 必要 — 用戶可能是透過 /stream 在看（不是自己的 POST SSE），切連線殺不到 claude。
    const abort = useCallback(async () => {
        controllerRef.current?.abort();
        if (!sessionId) return;
        try {
            const idToken = await auth.currentUser?.getIdToken();
            await fetch(`/ivr-builder/api/ivr/sessions/${encodeURIComponent(sessionId)}/abort`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${idToken}` },
            });
        } catch (e) {
            console.warn('[useSSEChat] abort POST failed:', e.message);
        }
    }, [sessionId]);

    // 清空對話記錄 — 保留最早 keep 則（預設 2 = 一開始建 avatar 的 user + assistant pair）
    const clearMemory = useCallback(async ({ keep = 2 } = {}) => {
        if (!sessionId) return;
        const { clearSessionMessages } = await import('../api/ivrAgents.js');
        await clearSessionMessages(sessionId, { keep });
        await loadHistory();
    }, [sessionId, loadHistory]);

    const messages = [
        ...history,
        ...(currentUser ? [currentUser] : []),
        ...(currentTurn ? [currentTurn] : []),
    ];

    return { messages, isStreaming, error, sendMessage, abort, reload: loadHistory, clearMemory, lastCompletedAt, hasMore, loadOlder, loadingOlder, turnStartedAt, currentTurn };
}
